import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const LOGO = '/dailyreps3.png';
const APP_URL = process.env.REACT_APP_URL || 'https://app.traindaily.com';
const STEPS = ['add-players', 'add-drill', 'create-challenge', 'invite-coaches', 'communicate', 'leaderboard'];

function getYouTubeThumbnail(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/);
  if (match) return `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`;
  return null;
}

export default function CoachOnboardingWizard() {
  const navigate = useNavigate();
  const { apiFetch } = useAuth();

  // Wizard state
  const [step, setStep] = useState('add-players');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [teamInfo, setTeamInfo] = useState(null);

  // Step 1: Add Players
  const [playerPath, setPlayerPath] = useState(null); // 'import' | 'manual' | 'invite'
  const [csvText, setCsvText] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [playerForm, setPlayerForm] = useState({ first_name: '', last_name: '', username: '', password: '', parent_email: '', birth_year: '' });
  const [addedPlayers, setAddedPlayers] = useState([]);
  const [copiedLink, setCopiedLink] = useState(false);

  // Step 2: Add Drill
  const [drillPath, setDrillPath] = useState(null); // 'library' | 'custom'
  const [libraryDrills, setLibraryDrills] = useState([]);
  const [librarySearch, setLibrarySearch] = useState('');
  const [librarySearchInput, setLibrarySearchInput] = useState('');
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [drillAdded, setDrillAdded] = useState(false);
  const [drillForm, setDrillForm] = useState({
    date: new Date().toISOString().split('T')[0],
    title: '', description: '', youtube_url: '',
    points_completion: '20', points_extra: '5',
  });

  // Step 3: Challenge
  const [challengeForm, setChallengeForm] = useState({ name: '', start_date: '', end_date: '' });
  const [challengeCreated, setChallengeCreated] = useState(false);

  // Step 4: Invite Coaches
  const [coachEmail, setCoachEmail] = useState('');
  const [invitedCoaches, setInvitedCoaches] = useState([]);

  // Step 5: Communicate
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [messageSent, setMessageSent] = useState(false);

  // Load team info on mount
  useEffect(() => {
    const loadTeam = async () => {
      try {
        const data = await apiFetch('/api/admin/teams/current');
        setTeamInfo(data.team);
      } catch (err) { console.error(err); }
    };
    loadTeam();
  }, []); // eslint-disable-line

  // Library search debounce
  useEffect(() => {
    const timer = setTimeout(() => setLibrarySearch(librarySearchInput), 300);
    return () => clearTimeout(timer);
  }, [librarySearchInput]);

  // Library search
  const loadLibrary = useCallback(async () => {
    setLibraryLoading(true);
    try {
      const params = new URLSearchParams();
      if (librarySearch.trim()) params.set('search', librarySearch.trim());
      const data = await apiFetch(`/api/library?${params.toString()}`);
      setLibraryDrills(data.drills || []);
    } catch (err) { console.error(err); }
    finally { setLibraryLoading(false); }
  }, [apiFetch, librarySearch]);

  useEffect(() => {
    if (drillPath === 'library') loadLibrary();
  }, [drillPath, librarySearch]); // eslint-disable-line

  // ── Completion / Skip ──

  const handleComplete = async () => {
    try {
      await apiFetch('/api/coach/complete-onboarding', { method: 'POST' });
    } catch (e) { /* non-critical */ }
    navigate('/admin');
  };

  // ── Navigation ──

  const goToStep = (s) => {
    setError('');
    setSuccess('');
    setStep(s);
  };

  const nextStep = () => {
    const idx = STEPS.indexOf(step);
    if (idx < STEPS.length - 1) goToStep(STEPS[idx + 1]);
  };

  const prevStep = () => {
    const idx = STEPS.indexOf(step);
    if (idx > 0) goToStep(STEPS[idx - 1]);
  };

  // ── Step 1: Import handlers ──

  const parseImportCSV = (text) => {
    const lines = text.trim().split('\n').filter(l => l.trim());
    if (lines.length === 0) return [];
    let start = 0;
    const firstLine = lines[0].toLowerCase();
    if (firstLine.includes('team') && firstLine.includes('name')) start = 1;
    return lines.slice(start).map(line => {
      const cells = line.split(',').map(c => c.trim());
      return { first_name: cells[3] || '', last_name: cells[4] || '', email: cells[5] || '' };
    });
  };

  const handleImportPreview = async () => {
    setError('');
    setImportResult(null);
    const rows = parseImportCSV(csvText);
    if (rows.length === 0) { setError('No data to import'); return; }
    setSaving(true);
    try {
      const data = await apiFetch('/api/admin/players/import', {
        method: 'POST',
        body: JSON.stringify({ players: rows, preview: true }),
      });
      setImportPreview(data);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleImportCommit = async () => {
    setError('');
    setSaving(true);
    try {
      const rows = parseImportCSV(csvText);
      const data = await apiFetch('/api/admin/players/import', {
        method: 'POST',
        body: JSON.stringify({ players: rows, preview: false }),
      });
      setImportResult(data);
      setImportPreview(null);
      setCsvText('');
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const downloadCredentials = (credentials) => {
    const header = 'First Name,Last Name,Username,Password';
    const csvRows = credentials.map(c =>
      `${c.first_name},${c.last_name},${c.username},${c.password}`
    );
    const csv = [header, ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'player-credentials.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result);
    reader.readAsText(file);
  };

  const downloadSampleCSV = () => {
    const csv = [
      'Team Name,Under 13?,Coach or Player,First Name,Last Name,Email',
      `${teamInfo?.name || 'My Team'},Yes,Player,Tommy,Johnson,parent@example.com`,
      `${teamInfo?.name || 'My Team'},Yes,Player,Sarah,Williams,`,
      `${teamInfo?.name || 'My Team'},No,Player,Alex,Brown,`,
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample-player-import.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Step 1: Manual add ──

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    setError('');
    if (!playerForm.first_name.trim() || !playerForm.last_name.trim()) { setError('First and last name are required'); return; }
    if (!playerForm.username.trim()) { setError('Username is required'); return; }
    if (!playerForm.password.trim()) { setError('Password is required'); return; }
    if (teamInfo?.has_under_13 && !playerForm.parent_email.trim()) { setError('Parent email is required for under-13 teams'); return; }
    setSaving(true);
    try {
      const { birth_year, ...pfRest } = playerForm;
      await apiFetch('/api/admin/players', {
        method: 'POST',
        body: JSON.stringify({ ...pfRest, ...(birth_year ? { birth_year: parseInt(birth_year, 10) } : {}) }),
      });
      setAddedPlayers(prev => [...prev, { ...playerForm }]);
      setSuccess(`Player ${playerForm.first_name} ${playerForm.last_name} added.`);
      setPlayerForm({ first_name: '', last_name: '', username: '', password: '', parent_email: '', birth_year: '' });
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // ── Step 1: Invite link ──

  const parentInviteUrl = teamInfo ? `${APP_URL}/parents/join/${teamInfo.join_code}` : '';
  const playerLoginUrl = teamInfo ? `${window.location.origin}/t/${teamInfo.join_code}` : '';

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    });
  };

  // ── Step 2: Library add drill ──

  const handleAddFromLibrary = async (drill) => {
    setError('');
    setSaving(true);
    try {
      await apiFetch('/api/admin/drills', {
        method: 'POST',
        body: JSON.stringify({
          date: new Date().toISOString().split('T')[0],
          title: drill.title,
          description: drill.description,
          youtube_url: drill.youtube_url,
          points_completion: 20,
          points_extra: 5,
        }),
      });
      setDrillAdded(true);
      setSuccess(`Drill "${drill.title}" added to your team.`);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // ── Step 2: Custom drill ──

  const handleCreateDrill = async (e) => {
    e.preventDefault();
    setError('');
    if (!drillForm.title.trim()) { setError('Drill title is required'); return; }
    if (!drillForm.date) { setError('Date is required'); return; }
    setSaving(true);
    try {
      await apiFetch('/api/admin/drills', {
        method: 'POST',
        body: JSON.stringify({
          ...drillForm,
          points_completion: parseInt(drillForm.points_completion) || 20,
          points_extra: parseInt(drillForm.points_extra) || 5,
        }),
      });
      setDrillAdded(true);
      setSuccess(`Drill "${drillForm.title}" created.`);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // ── Step 3: Challenge ──

  const handleStartDateChange = (startDate) => {
    setChallengeForm(prev => {
      const updated = { ...prev, start_date: startDate };
      if (startDate && !prev.end_date) {
        const start = new Date(startDate);
        start.setMonth(start.getMonth() + 11);
        updated.end_date = start.toISOString().split('T')[0];
      }
      return updated;
    });
  };

  const handleCreateChallenge = async (e) => {
    e.preventDefault();
    setError('');
    if (!challengeForm.name.trim()) { setError('Challenge name is required'); return; }
    if (!challengeForm.start_date) { setError('Start date is required'); return; }
    if (!challengeForm.end_date) { setError('End date is required'); return; }
    setSaving(true);
    try {
      const data = await apiFetch('/api/admin/seasons', {
        method: 'POST',
        body: JSON.stringify(challengeForm),
      });
      // Activate the challenge
      const challengeId = data.challenge?.id || data.id;
      if (challengeId) {
        await apiFetch(`/api/admin/seasons/${challengeId}/activate`, { method: 'PUT' });
      }
      setChallengeCreated(true);
      setSuccess(`Challenge "${challengeForm.name}" created and activated.`);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // ── Step 4: Invite coach ──

  const handleInviteCoach = async () => {
    if (!coachEmail.trim()) return;
    setError('');
    setSaving(true);
    try {
      const data = await apiFetch('/api/admin/coaches', {
        method: 'POST',
        body: JSON.stringify({ email: coachEmail }),
      });
      setInvitedCoaches(prev => [...prev, coachEmail]);
      setSuccess(data.linked ? data.message : `Invitation sent to ${coachEmail}.`);
      setCoachEmail('');
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // ── Step 5: Push notification ──

  const handleSendPush = async () => {
    if (!pushTitle.trim() || !pushBody.trim()) { setError('Title and message are required'); return; }
    setError('');
    setSaving(true);
    try {
      await apiFetch('/api/admin/push/send', {
        method: 'POST',
        body: JSON.stringify({ title: pushTitle, body: pushBody }),
      });
      setMessageSent(true);
      setSuccess('Push notification sent to your team.');
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // ── Progress dots ──

  const renderProgress = () => {
    const currentIdx = STEPS.indexOf(step);
    return (
      <div className="onboarding-progress" style={{ marginBottom: 24 }}>
        {STEPS.map((s, i) => (
          <div
            key={s}
            className={`onboarding-dot${i <= currentIdx ? ' active' : ''}${s === step ? ' current' : ''}${i < currentIdx ? ' clickable' : ''}`}
            onClick={() => i < currentIdx && goToStep(STEPS[i])}
            style={{ width: 28, height: 28, fontSize: '0.8rem' }}
          >
            {i < currentIdx ? <i className="fas fa-check" style={{ fontSize: '0.65rem' }}></i> : i + 1}
          </div>
        ))}
      </div>
    );
  };

  // ── Step nav footer ──

  const renderNav = (showSkipStep = false) => (
    <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      {STEPS.indexOf(step) > 0 ? (
        <button className="onboarding-skip" onClick={prevStep} style={{ marginTop: 0 }}>Back</button>
      ) : <div />}
      <div style={{ display: 'flex', gap: 12 }}>
        {showSkipStep && (
          <button className="onboarding-skip" onClick={nextStep} style={{ marginTop: 0 }}>Skip this step</button>
        )}
        <button className="onboarding-skip" onClick={handleComplete} style={{ marginTop: 0 }}>Skip for now</button>
      </div>
    </div>
  );

  // ── Render ──

  return (
    <div className="order-page">
      <header className="order-header">
        <div className="order-wrap">
          <div className="order-logo-row">
            <img src={LOGO} alt="Train Daily" className="order-logo" />
            <span className="order-brand">Train Daily</span>
          </div>
        </div>
      </header>
      <div className="order-wrap" style={{ maxWidth: 640, padding: '0 16px' }}>
        {renderProgress()}

        {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
        {success && <div className="alert alert-success" style={{ marginBottom: 16 }}>{success}</div>}

        {/* ══════════════════════════════════════════════════
            STEP 1: ADD PLAYERS (club_pays)
            ══════════════════════════════════════════════════ */}
        {step === 'add-players' && !playerPath && teamInfo?.billing_model !== 'parent_pays' && (
          <div className="onboarding-card">
            <h2>Let's Get Started</h2>
            <p className="onboarding-subtitle">
              This setup gets you started on the right foot. You can always come back and change things later.
            </p>
            <h3 style={{ marginTop: 20, marginBottom: 12 }}>Add Your Players</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button className="onboarding-path-btn" onClick={() => setPlayerPath('import')}>
                <i className="fas fa-file-csv" style={{ fontSize: '1.5rem', color: 'var(--op-orange)' }}></i>
                <div>
                  <strong style={{ display: 'block', fontSize: '1rem', marginBottom: 2 }}>Import Roster</strong>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Upload a CSV file with player info</span>
                </div>
              </button>
              <button className="onboarding-path-btn" onClick={() => setPlayerPath('manual')}>
                <i className="fas fa-user-plus" style={{ fontSize: '1.5rem', color: 'var(--blue)' }}></i>
                <div>
                  <strong style={{ display: 'block', fontSize: '1rem', marginBottom: 2 }}>Add Players Manually</strong>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Enter each player one at a time</span>
                </div>
              </button>
              <button className="onboarding-path-btn" onClick={() => setPlayerPath('invite')}>
                <i className="fas fa-link" style={{ fontSize: '1.5rem', color: '#16a34a' }}></i>
                <div>
                  <strong style={{ display: 'block', fontSize: '1rem', marginBottom: 2 }}>Invite Players via Link</strong>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Share a link for parents or players to join</span>
                </div>
              </button>
            </div>
            <button className="onboarding-skip" onClick={handleComplete}>Skip for now</button>
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            STEP 1: INVITE PLAYERS (parent_pays)
            ══════════════════════════════════════════════════ */}
        {step === 'add-players' && !playerPath && teamInfo?.billing_model === 'parent_pays' && (
          <div className="onboarding-card">
            <h2>Let's Get Started</h2>
            <p className="onboarding-subtitle">
              This onboarding wizard takes 2-5 minutes and gets you started on the right foot.
            </p>
            <h3 style={{ marginTop: 20, marginBottom: 12 }}>Let's add your first player(s)</h3>
            <button className="onboarding-path-btn" onClick={() => setPlayerPath('invite')}>
              <i className="fas fa-link" style={{ fontSize: '1.5rem', color: '#16a34a' }}></i>
              <div>
                <strong style={{ display: 'block', fontSize: '1rem', marginBottom: 2 }}>Invite Players</strong>
                <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Share a link for parents to sign up their players</span>
              </div>
            </button>
            <button className="onboarding-skip" onClick={handleComplete}>Skip for now</button>
          </div>
        )}

        {/* Import sub-path */}
        {step === 'add-players' && playerPath === 'import' && !importResult && (
          <div className="onboarding-card">
            <h2>Import Roster</h2>
            <p className="onboarding-subtitle">
              Upload a CSV to add players. Columns (comma-separated):
            </p>
            <code style={{ display: 'block', fontSize: '0.8rem', background: '#f1f5f9', padding: 10, borderRadius: 6, marginBottom: 12, color: 'var(--op-orange)' }}>
              Team Name, Under 13?, Coach or Player, First Name, Last Name, Email
            </code>
            <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 12 }}>
              For under-13 players, email is the parent email for consent. A header row is optional.
            </p>
            <div style={{ marginBottom: 12 }}>
              <button className="btn btn-orange btn-sm" onClick={downloadSampleCSV} style={{ marginBottom: 8 }}>Download Sample CSV</button>
            </div>
            <div style={{ marginBottom: 12 }}>
              <input type="file" accept=".csv,.txt" onChange={handleFileUpload} style={{ marginBottom: 8 }} />
            </div>
            <textarea
              className="onboarding-input"
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              placeholder="Or paste CSV data here..."
              rows={8}
              style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
            />
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button className="btn btn-blue btn-sm" onClick={handleImportPreview} disabled={saving || !csvText.trim()}>
                {saving ? 'Validating...' : 'Preview Import'}
              </button>
              {importPreview && (!importPreview.errors || importPreview.errors.length === 0) && (
                <button className="btn btn-orange btn-sm" onClick={handleImportCommit} disabled={saving}>
                  {saving ? 'Importing...' : 'Commit Import'}
                </button>
              )}
            </div>

            {importPreview && (
              <div style={{ marginTop: 16, padding: 16, background: '#f8fafc', borderRadius: 8 }}>
                <h3 style={{ fontSize: '0.95rem', marginBottom: 8 }}>Preview</h3>
                <div style={{ fontSize: '0.85rem', marginBottom: 8 }}>
                  <div>Players to create: <strong>{importPreview.summary?.players_to_create || importPreview.summary?.players || importPreview.summary?.total || 0}</strong></div>
                  {importPreview.summary?.duplicates_skipped > 0 && (
                    <div style={{ color: '#6c757d' }}>Existing players skipped: <strong>{importPreview.summary.duplicates_skipped}</strong></div>
                  )}
                </div>
                {importPreview.errors && importPreview.errors.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {importPreview.errors.map((err, i) => (
                      <div key={i} style={{ fontSize: '0.8rem', color: '#e74c3c', marginBottom: 4 }}>
                        Row {err.row}: {err.error || err.message}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              <button className="onboarding-skip" onClick={() => { setPlayerPath(null); setImportPreview(null); setCsvText(''); }} style={{ marginTop: 0 }}>Back</button>
            </div>
            {renderNav()}
          </div>
        )}

        {/* Import done */}
        {step === 'add-players' && playerPath === 'import' && importResult && (
          <div className="onboarding-card">
            <h2 style={{ color: '#16a34a' }}>Import Complete</h2>
            <div style={{ fontSize: '0.95rem', marginBottom: 16 }}>
              <div>Players created: <strong>{importResult.summary?.created || importResult.credentials?.length || 0}</strong></div>
            </div>
            {importResult.credentials && importResult.credentials.length > 0 && (
              <button className="btn btn-blue btn-sm" onClick={() => downloadCredentials(importResult.credentials)} style={{ marginBottom: 16 }}>
                Download Player Logins
              </button>
            )}
            <button className="btn btn-orange" onClick={nextStep} style={{ width: '100%', marginTop: 8 }}>
              Continue to Drills
            </button>
            {renderNav()}
          </div>
        )}

        {/* Manual add sub-path */}
        {step === 'add-players' && playerPath === 'manual' && (
          <div className="onboarding-card">
            <h2>Add Players Manually</h2>
            <p className="onboarding-subtitle">Add players one at a time. You can add more later from your roster page.</p>
            <form onSubmit={handleAddPlayer}>
              <div className="onboarding-row">
                <div className="onboarding-field">
                  <label>First Name</label>
                  <input className="onboarding-input" value={playerForm.first_name} onChange={(e) => setPlayerForm(prev => ({ ...prev, first_name: e.target.value }))} required />
                </div>
                <div className="onboarding-field">
                  <label>Last Name</label>
                  <input className="onboarding-input" value={playerForm.last_name} onChange={(e) => setPlayerForm(prev => ({ ...prev, last_name: e.target.value }))} required />
                </div>
              </div>
              <div className="onboarding-field">
                <label>Birth Year <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                <select className="onboarding-input" value={playerForm.birth_year} onChange={(e) => setPlayerForm(prev => ({ ...prev, birth_year: e.target.value }))}>
                  <option value="">--</option>
                  {Array.from({ length: 23 }, (_, i) => { const y = new Date().getFullYear() - 3 - i; return <option key={y} value={y}>{y}</option>; })}
                </select>
              </div>
              <div className="onboarding-row">
                <div className="onboarding-field">
                  <label>Username</label>
                  <input className="onboarding-input" value={playerForm.username} onChange={(e) => setPlayerForm(prev => ({ ...prev, username: e.target.value }))} required />
                </div>
                <div className="onboarding-field">
                  <label>Password</label>
                  <input className="onboarding-input" type="text" value={playerForm.password} onChange={(e) => setPlayerForm(prev => ({ ...prev, password: e.target.value }))} required />
                </div>
              </div>
              {teamInfo?.has_under_13 && (
                <div className="onboarding-field">
                  <label>Parent Email</label>
                  <input className="onboarding-input" type="email" value={playerForm.parent_email} onChange={(e) => setPlayerForm(prev => ({ ...prev, parent_email: e.target.value }))} placeholder="Required for under-13 teams" required />
                </div>
              )}
              <button className="btn btn-orange" type="submit" disabled={saving} style={{ width: '100%' }}>
                {saving ? 'Adding...' : 'Add Player'}
              </button>
            </form>

            {addedPlayers.length > 0 && (
              <div style={{ marginTop: 16, borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
                <h3 style={{ fontSize: '0.85rem', color: '#334155', marginBottom: 8 }}>Players Added ({addedPlayers.length})</h3>
                {addedPlayers.map((p, i) => (
                  <div key={i} style={{ fontSize: '0.85rem', padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                    {p.first_name} {p.last_name} <span style={{ color: '#64748b' }}>({p.username})</span>
                  </div>
                ))}
                <button className="btn btn-blue" onClick={nextStep} style={{ width: '100%', marginTop: 16 }}>
                  Continue to Drills
                </button>
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              <button className="onboarding-skip" onClick={() => setPlayerPath(null)} style={{ marginTop: 0 }}>Back</button>
            </div>
            {renderNav()}
          </div>
        )}

        {/* Invite via link sub-path */}
        {step === 'add-players' && playerPath === 'invite' && (
          <div className="onboarding-card">
            <h2>Invite Players via Link</h2>
            <p className="onboarding-subtitle">
              Share these links with parents or players so they can join {teamInfo?.name || 'your team'}.
            </p>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem', display: 'block', marginBottom: 6 }}>Parent Signup Link</label>
              <div style={{ background: '#f1f5f9', padding: '10px 14px', borderRadius: 8, fontSize: '0.8rem', wordBreak: 'break-all', fontFamily: 'monospace', marginBottom: 8 }}>
                {parentInviteUrl}
              </div>
              <button className="btn btn-blue btn-sm" onClick={() => handleCopyLink(parentInviteUrl)}>
                {copiedLink ? 'Copied!' : 'Copy Link'}
              </button>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem', display: 'block', marginBottom: 6 }}>Player Login Link</label>
              <div style={{ background: '#f1f5f9', padding: '10px 14px', borderRadius: 8, fontSize: '0.8rem', wordBreak: 'break-all', fontFamily: 'monospace', marginBottom: 8 }}>
                {playerLoginUrl}
              </div>
            </div>

            <button className="btn btn-orange" onClick={nextStep} style={{ width: '100%' }}>
              Continue to Drills
            </button>
            <div style={{ marginTop: 16 }}>
              <button className="onboarding-skip" onClick={() => setPlayerPath(null)} style={{ marginTop: 0 }}>Back</button>
            </div>
            {renderNav()}
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            STEP 2: ADD DRILL
            ══════════════════════════════════════════════════ */}
        {step === 'add-drill' && !drillPath && !drillAdded && (
          <div className="onboarding-card">
            <h2>Add Your First Drill</h2>
            <p className="onboarding-subtitle">
              Drills are the daily training activities your players complete. Pick one from the library or create your own.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
              <button className="onboarding-path-btn" onClick={() => setDrillPath('library')}>
                <i className="fas fa-book-open" style={{ fontSize: '1.5rem', color: 'var(--op-orange)' }}></i>
                <div>
                  <strong style={{ display: 'block', fontSize: '1rem', marginBottom: 2 }}>Browse Drill Library</strong>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Pick from hundreds of pre-made drills</span>
                </div>
              </button>
              <button className="onboarding-path-btn" onClick={() => setDrillPath('custom')}>
                <i className="fas fa-plus-circle" style={{ fontSize: '1.5rem', color: 'var(--blue)' }}></i>
                <div>
                  <strong style={{ display: 'block', fontSize: '1rem', marginBottom: 2 }}>Create Custom Drill</strong>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Build your own drill from scratch</span>
                </div>
              </button>
            </div>
            {renderNav()}
          </div>
        )}

        {/* Library sub-path */}
        {step === 'add-drill' && drillPath === 'library' && !drillAdded && (
          <div className="onboarding-card">
            <h2>Browse Drill Library</h2>
            <p className="onboarding-subtitle">Search for a drill and add it to your team.</p>
            <div className="onboarding-field">
              <input
                className="onboarding-input"
                placeholder="Search drills..."
                value={librarySearchInput}
                onChange={(e) => setLibrarySearchInput(e.target.value)}
              />
            </div>
            {libraryLoading && <div style={{ textAlign: 'center', padding: 20, color: '#64748b' }}>Loading...</div>}
            {!libraryLoading && libraryDrills.length === 0 && (
              <div style={{ textAlign: 'center', padding: 20, color: '#64748b' }}>No drills found. Try a different search.</div>
            )}
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {libraryDrills.slice(0, 20).map(drill => (
                <div key={drill.id} style={{ display: 'flex', gap: 12, padding: '12px 0', borderBottom: '1px solid #f1f5f9', alignItems: 'center' }}>
                  {getYouTubeThumbnail(drill.youtube_url) && (
                    <img src={getYouTubeThumbnail(drill.youtube_url)} alt="" style={{ width: 80, height: 45, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: '0.9rem', display: 'block' }}>{drill.title}</strong>
                    {drill.description && (
                      <span style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {drill.description}
                      </span>
                    )}
                  </div>
                  <button className="btn btn-orange btn-sm" onClick={() => handleAddFromLibrary(drill)} disabled={saving} style={{ flexShrink: 0 }}>
                    Add
                  </button>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 16 }}>
              <button className="onboarding-skip" onClick={() => setDrillPath(null)} style={{ marginTop: 0 }}>Back</button>
            </div>
            {renderNav()}
          </div>
        )}

        {/* Custom drill sub-path */}
        {step === 'add-drill' && drillPath === 'custom' && !drillAdded && (
          <div className="onboarding-card">
            <h2>Create Custom Drill</h2>
            <p className="onboarding-subtitle">Create a training drill for your team. You can add quiz questions later from the Drills page.</p>
            <form onSubmit={handleCreateDrill}>
              <div className="onboarding-field">
                <label>Date</label>
                <input className="onboarding-input" type="date" value={drillForm.date} onChange={(e) => setDrillForm(prev => ({ ...prev, date: e.target.value }))} required />
              </div>
              <div className="onboarding-field">
                <label>Title</label>
                <input className="onboarding-input" value={drillForm.title} onChange={(e) => setDrillForm(prev => ({ ...prev, title: e.target.value }))} placeholder="e.g. Ball Mastery - Inside/Outside Touches" required />
              </div>
              <div className="onboarding-field">
                <label>Description</label>
                <textarea className="onboarding-input" value={drillForm.description} onChange={(e) => setDrillForm(prev => ({ ...prev, description: e.target.value }))} placeholder="What should the player do?" rows={3} />
              </div>
              <div className="onboarding-field">
                <label>YouTube Video URL (optional)</label>
                <input className="onboarding-input" value={drillForm.youtube_url} onChange={(e) => setDrillForm(prev => ({ ...prev, youtube_url: e.target.value }))} placeholder="https://youtube.com/watch?v=..." />
              </div>
              <div className="onboarding-row">
                <div className="onboarding-field">
                  <label>Points (Completion)</label>
                  <input className="onboarding-input" type="number" value={drillForm.points_completion} onChange={(e) => setDrillForm(prev => ({ ...prev, points_completion: e.target.value }))} />
                </div>
                <div className="onboarding-field">
                  <label>Points (Extra Credit)</label>
                  <input className="onboarding-input" type="number" value={drillForm.points_extra} onChange={(e) => setDrillForm(prev => ({ ...prev, points_extra: e.target.value }))} />
                </div>
              </div>
              <button className="btn btn-orange" type="submit" disabled={saving} style={{ width: '100%' }}>
                {saving ? 'Creating...' : 'Create Drill'}
              </button>
            </form>
            <div style={{ marginTop: 16 }}>
              <button className="onboarding-skip" onClick={() => setDrillPath(null)} style={{ marginTop: 0 }}>Back</button>
            </div>
            {renderNav()}
          </div>
        )}

        {/* Drill added (both paths) */}
        {step === 'add-drill' && drillAdded && (
          <div className="onboarding-card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>&#9989;</div>
            <h2>Drill Added</h2>
            <p className="onboarding-subtitle">Your players will see this drill on their daily training page.</p>
            <button className="btn btn-orange" onClick={nextStep} style={{ width: '100%', marginTop: 16 }}>
              Continue to Challenges
            </button>
            {renderNav()}
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            STEP 3: CREATE CHALLENGE
            ══════════════════════════════════════════════════ */}
        {step === 'create-challenge' && !challengeCreated && (
          <div className="onboarding-card">
            <h2>Set Up Your First Challenge</h2>
            <p className="onboarding-subtitle">
              Challenges are timed competitions that motivate players. Points earned during a challenge show up on the leaderboard. Set a start and end date to kick things off.
            </p>
            <form onSubmit={handleCreateChallenge}>
              <div className="onboarding-field">
                <label>Challenge Name</label>
                <input className="onboarding-input" value={challengeForm.name} onChange={(e) => setChallengeForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. Fall 2026 Challenge" required />
              </div>
              <div className="onboarding-row">
                <div className="onboarding-field">
                  <label>Start Date</label>
                  <input className="onboarding-input" type="date" value={challengeForm.start_date} onChange={(e) => handleStartDateChange(e.target.value)} required />
                </div>
                <div className="onboarding-field">
                  <label>End Date</label>
                  <input className="onboarding-input" type="date" value={challengeForm.end_date} onChange={(e) => setChallengeForm(prev => ({ ...prev, end_date: e.target.value }))} required />
                </div>
              </div>
              <button className="btn btn-orange" type="submit" disabled={saving} style={{ width: '100%' }}>
                {saving ? 'Creating...' : 'Create & Activate Challenge'}
              </button>
            </form>
            {renderNav()}
          </div>
        )}

        {step === 'create-challenge' && challengeCreated && (
          <div className="onboarding-card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>&#127942;</div>
            <h2>Challenge Active</h2>
            <p className="onboarding-subtitle">Your players will start earning points on the leaderboard.</p>
            <button className="btn btn-orange" onClick={nextStep} style={{ width: '100%', marginTop: 16 }}>
              Continue
            </button>
            {renderNav()}
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            STEP 4: INVITE COACHES
            ══════════════════════════════════════════════════ */}
        {step === 'invite-coaches' && (
          <div className="onboarding-card">
            <h2>Invite Other Coaches</h2>
            <p className="onboarding-subtitle">
              Add other coaches to help manage {teamInfo?.name || 'your team'}. They'll get an email invitation to create their account.
            </p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input
                className="onboarding-input"
                type="email"
                placeholder="Coach email address"
                value={coachEmail}
                onChange={(e) => setCoachEmail(e.target.value)}
                style={{ flex: 1, marginBottom: 0 }}
              />
              <button className="btn btn-orange btn-sm" onClick={handleInviteCoach} disabled={saving || !coachEmail.trim()}>
                Invite
              </button>
            </div>

            {invitedCoaches.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                {invitedCoaches.map((email, i) => (
                  <div key={i} style={{ fontSize: '0.85rem', color: '#16a34a', padding: '4px 0' }}>
                    <i className="fas fa-check" style={{ marginRight: 6 }}></i>{email}
                  </div>
                ))}
              </div>
            )}

            <button className="btn btn-blue" onClick={nextStep} style={{ width: '100%' }}>
              Continue
            </button>
            {renderNav(true)}
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            STEP 5: COMMUNICATE
            ══════════════════════════════════════════════════ */}
        {step === 'communicate' && !messageSent && (
          <div className="onboarding-card">
            <h2>Communicate with Your Team</h2>
            <p className="onboarding-subtitle">
              Send push notifications to keep your players engaged. You can also send emails from the Communication page on your dashboard.
            </p>
            <div className="onboarding-field">
              <label>Title</label>
              <input className="onboarding-input" value={pushTitle} onChange={(e) => setPushTitle(e.target.value)} placeholder="e.g. Welcome to Train Daily!" />
            </div>
            <div className="onboarding-field">
              <label>Message</label>
              <textarea className="onboarding-input" value={pushBody} onChange={(e) => setPushBody(e.target.value)} placeholder="e.g. Let's get started with your first drill today!" rows={3} />
            </div>
            <button className="btn btn-orange" onClick={handleSendPush} disabled={saving || !pushTitle.trim() || !pushBody.trim()} style={{ width: '100%' }}>
              {saving ? 'Sending...' : 'Send Push Notification'}
            </button>
            {renderNav(true)}
          </div>
        )}

        {step === 'communicate' && messageSent && (
          <div className="onboarding-card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>&#128227;</div>
            <h2>Message Sent</h2>
            <p className="onboarding-subtitle">Your team received the push notification.</p>
            <button className="btn btn-orange" onClick={nextStep} style={{ width: '100%', marginTop: 16 }}>
              Continue
            </button>
            {renderNav()}
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            STEP 6: LEADERBOARD & REPORTS
            ══════════════════════════════════════════════════ */}
        {step === 'leaderboard' && (
          <div className="onboarding-card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>&#127881;</div>
            <h2>You're All Set!</h2>
            <p className="onboarding-subtitle">
              Check out your Leaderboard to see player standings, and Reports for detailed analytics on team activity.
            </p>
            <div style={{ display: 'flex', gap: 12, marginBottom: 24, marginTop: 20 }}>
              <a href="/admin/leaderboard" target="_blank" rel="noopener noreferrer" className="btn btn-blue" style={{ flex: 1, textDecoration: 'none', textAlign: 'center' }}>
                View Leaderboard
              </a>
              <a href="/admin/reports" target="_blank" rel="noopener noreferrer" className="btn btn-outline" style={{ flex: 1, textDecoration: 'none', textAlign: 'center' }}>
                View Reports
              </a>
            </div>
            <button className="btn btn-orange" onClick={handleComplete} style={{ width: '100%', padding: '14px 24px', fontSize: '1.05rem' }}>
              Go to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
